import { useEffect, useState, useRef } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { Users, Zap, Target, Clock } from "lucide-react";

interface Stat {
  icon: typeof Users;
  value: number;
  suffix: string;
  label: string;
  color: string;
}

const stats: Stat[] = [
  {
    icon: Users,
    value: 5000,
    suffix: "+",
    label: "Contacts Extracted",
    color: "text-brand-blue",
  },
  {
    icon: Clock,
    value: 30,
    suffix: "s",
    label: "Average Extraction Time",
    color: "text-brand-orange",
  },
  {
    icon: Target,
    value: 99,
    suffix: "%",
    label: "Accuracy Rate",
    color: "text-secondary",
  },
  {
    icon: Zap,
    value: 10,
    suffix: "x",
    label: "Faster Than Manual",
    color: "text-brand-cyan",
  },
];

const AnimatedCounter = ({ 
  value, 
  suffix, 
  isVisible 
}: { 
  value: number; 
  suffix: string; 
  isVisible: boolean;
}) => {
  const [count, setCount] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    
    if (prefersReducedMotion) {
      setCount(value);
      hasAnimated.current = true;
      return;
    }

    hasAnimated.current = true;
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, value, prefersReducedMotion]);

  return (
    <span className="tabular-nums">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

export const StatsCounter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const { ref: headerRef, animationClass: headerAnimation } = useScrollAnimation<HTMLDivElement>({
    type: 'slide-up',
    threshold: 0.3,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-gradient-to-b from-background to-muted/30">
      <div className="container px-4">
        <div 
          ref={headerRef}
          className={`text-center max-w-2xl mx-auto mb-12 md:mb-16 ${headerAnimation}`}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Numbers That Speak
          </h2>
          <p className="text-lg text-muted-foreground">
            Join professionals who save hours every week with intelligent contact extraction.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-5xl mx-auto">
          {stats.map((stat, index) => {
            const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
              type: 'scale',
              delay: index * 150,
              threshold: 0.2,
            });

            return (
              <div
                key={index}
                ref={ref}
                className={`text-center p-6 md:p-8 rounded-2xl bg-card border border-border shadow-card hover:shadow-elevated transition-all duration-300 group ${animationClass}`}
              >
                <div className={`inline-flex p-3 rounded-xl bg-muted mb-4 group-hover:scale-110 transition-transform ${stat.color}`}>
                  <stat.icon className="w-6 h-6 md:w-7 md:h-7" />
                </div>
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2">
                  <AnimatedCounter 
                    value={stat.value} 
                    suffix={stat.suffix} 
                    isVisible={isVisible} 
                  />
                </div>
                <p className="text-sm md:text-base text-muted-foreground">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
